/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink, SFTPConnection} from '@ion/core/src/types/entities';
import {
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {
  useColumnChooser
} from '@ion/dls/src/grid/useColumnChooser';
import {
  useColumnState
} from '@ion/dls/src/grid/useColumnState';
import {useGetSubAccounts} from '@ion/query/src/hooks/useGetSubAccounts';
import {useSFTPConfigurations} from '@ion/query/src/hooks/useSFTPConfigurations';

// specifies the props that the SFTPConfigurationDetailViewGrid component
export interface SFTPConfigurationDetailViewGridProps {
  account: AccountLink;
  onSelectionChange?: (rows) => void;
  omitSubAccounts?: boolean;
};

// represents the default sorting configuration for a grid or table
const defaultSort: SortModelType = {
  colId : 'serverName',
  sort  : 'asc'
};

/**
 * React component for rendering a grid displaying SFTP configurations and it is responsible for
 * rendering a grid that displays SFTP configurations associated with a specific account
 *
 * ```typescript
 *
 *  import {SFTPConfigurationDetailViewGrid} from './SFTPConfigurationDetailViewGrid';
 *
 *  <SFTPConfigurationDetailViewGrid account={account} onSelectionChange={onSelectionChange} />
 *
 * ```
 * @param account - An account link representing the associated account for the configurations.
 * @param onSelectionChange - A callback function triggered when row selections in the grid change.
 * @param ref - React ref object for accessing imperative methods (e.g., toggleColumnChooser).
 */
export const SFTPConfigurationDetailViewGrid = ({
  account,
  omitSubAccounts = true,
  onSelectionChange
}: SFTPConfigurationDetailViewGridProps, ref) => {
  const accountId     = useMemo(() => entityId(account), [account]);
  const accoutListRef = useRef({});

  // useGetSubAccounts hook fetches sub-accounts for the specified parent account.
  const {
    subAccounts,
    isLoading: isAccountLoading
  } = useGetSubAccounts({account, enabled: !!accountId});

  useEffect(() => {
    if (!isAccountLoading) {
      // format the account list as per need `[key: accountId, value: accountTitle]`.
      const items = {};
      subAccounts?.forEach(({href, title}) => {
        items[entityId(href)] = title;
      });
      accoutListRef.current = items;
    }
  }, [subAccounts, isAccountLoading]);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(omitSubAccounts),
    columnState       : null
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  useImperativeHandle(ref, () => ({
    toggleColumnChooser
  }), [toggleColumnChooser]);

  const onColumnSelectionsUpdate = useCallback((columns: ColDef[]) => {
    onColumnChooserSave(columns);
  }, [onColumnChooserSave]);

  const onColumnChooserClose = useCallback(() => {
    toggleColumnChooser(false);
  }, [toggleColumnChooser]);

  const {
    configurations,
    isLoading
  } = useSFTPConfigurations({accountId, enabled: !!accountId});

  const onSelectionChanged = useCallback((event) => {
    onSelectionChange(event.api.getSelectedRows());
  }, [onSelectionChange]);

  const constructsGridRows = useCallback((configurations: SFTPConnection[]) => (
    omitSubAccounts ? configurations : configurations?.map(configuration => ({
      ...configuration,
      subAccounts : configuration.allowedAccounts?.filter?.(id => accountId !== id)
        .map(accountId => (
          accoutListRef.current[accountId] || accountId
        )).join(', ')
    }))
  ), [accountId, omitSubAccounts]);

  return (
    <IonGrid onColumnChooserSave={onColumnSelectionsUpdate}
      onColumnChooserClose={onColumnChooserClose}
      rowModelType={RowModelTypes.ClientSide}
      isLoading={isLoading || isAccountLoading}
      defaultSort={defaultSort}
      rowData={configurations}
      augmentItems={constructsGridRows}
      gridOptions={{onSelectionChanged}}
      totalRows={configurations?.length}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      showHeaderCheckbox={false}
      viewSubGroups={false}
      rowMenusEnabled={false}
      rowHeight={40}
      checkboxesEnabled
      omitHeader />
  );
};

/**
 * The function generates an array of column definitions (ColDef) for a grid or table.
 * The columns seem to represent various fields associated with SFTP configuration
 */
const getColumnDef = (isMobile = false, omitSubAccounts = true): ColDef[] => ([{
  headerName : translate('admin-groups:sftpEditor.form.serverName.fieldLabel'),
  field      : 'serverName',
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : false
}, {
  headerName : translate('admin-groups:sftpEditor.form.host.fieldLabel'),
  field      : 'host',
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : false
}, {
  headerName : translate('admin-groups:sftpEditor.form.port.fieldLabel'),
  field      : 'port',
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : false
}, {
  headerName : translate('admin-groups:sftpEditor.form.username.fieldLabel'),
  field      : 'username',
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : false
}, {
  headerName : translate('admin-groups:sftpEditor.form.password.fieldLabel'),
  field      : 'password',
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : true
}, {
  headerName : translate('admin-groups:sftpEditor.form.privateKey.fieldLabel'),
  field      : 'privateKey',
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : true
}, ...(omitSubAccounts ? [] : [{
  headerName : translate('common:accountChooser.label'),
  field      : 'subAccounts',
  sortable   : true,
  filter     : false,
  resizable  : true,
  unSortIcon : true,
  hide       : false
}])]);

/**
 * This function appears to be designed to generate column definitions
 * for a grid or table, possibly for both desktop and mobile views.
 */
const getColumnDefinitions = (omitSubAccounts = true) => ({
  desktop : resolveDefinitions(getColumnDef()),
  mobile  : resolveDefinitions(getColumnDef(true, omitSubAccounts))
});

export default forwardRef(SFTPConfigurationDetailViewGrid);
